import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { REPORT_TEMPLATE_KEYS, TEMPLATE_META, DEFAULT_REPORT_TEMPLATES, type ReportTemplateKey } from '../../utils/reportTemplates';

export interface FactoryItem {
  id?: string;
  name: string;
  region: string;
  address?: string;
  contactPerson?: string;
  designation?: string;
  phoneNumber?: string;
  inspections: number;
  usersCount: number;
  storageGB: number;
  storageLabel?: string;
  appVersion: string;
  outdatedDevices: number;
  status: 'Optimal' | 'Warning' | 'Critical';
  uptime: string;
  enrolledDate: string;
  allowedUsers?: number;
  subscriptionStatus?: 'Trial' | 'Active' | 'Grace' | 'Expired';
  subscriptionStart?: string;
  subscriptionEnd?: string;
  isDisabled?: boolean;
  isArchived?: boolean;
  archivedAt?: string | null;
  allowedReportTemplates?: ReportTemplateKey[];
}

export interface UserItem {
  id: string;
  name: string;
  email: string;
  password?: string;
  employeeId: string;
  lastLoggedIn: string;
  inspectionsCount: number;
  appVersion: string;
  osVersion?: string;
  deviceName?: string;
  usageSeconds?: number;
  status: 'Active' | 'Disabled';
  factoryName: string;
  role?: 'Admin' | 'Staff';
  modules?: string[];
  profileImage?: string;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  if (dateStr.includes(' ') && dateStr.length === 11 && !dateStr.includes('-')) {
    return dateStr;
  }
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const year = parts[0];
  const monthNum = parseInt(parts[1], 10);
  const day = parts[2];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = months[monthNum - 1] || 'Jan';
  const formattedDay = day.padStart(2, '0');
  return `${formattedDay} ${monthName} ${year}`;
};

const mapMonthToNum = (monthName: string): string => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const idx = months.indexOf(monthName);
  if (idx === -1) return '01';
  return (idx + 1).toString().padStart(2, '0');
};

const dateToYMD = (dateStr: string): string => {
  if (!dateStr) return '';
  if (dateStr.includes('-') && dateStr.split('-').length === 3) {
    return dateStr;
  }
  const parts = dateStr.trim().split(/\s+/);
  if (parts.length === 3) {
    const day = parts[0].padStart(2, '0');
    const month = mapMonthToNum(parts[1]);
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {}
  return '';
};

const ymdToReadable = (ymdStr: string): string => {
  if (!ymdStr) return '';
  const parts = ymdStr.split('-');
  if (parts.length !== 3) return ymdStr;
  const year = parts[0];
  const monthNum = parseInt(parts[1], 10) || 1;
  const day = parts[2];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = months[monthNum - 1] || 'Jan';
  const formattedDay = parseInt(day, 10).toString().padStart(2, '0');
  return `${formattedDay} ${monthName} ${year}`;
};

const formatUsageTime = (seconds?: number): string => {
  if (!seconds || seconds <= 0) return 'No usage recorded';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m active`;
  return `${minutes}m active`;
};

const getUserAvatarUrl = (name: string, profileImage?: string) => {
  if (profileImage) return profileImage;
  // Fallback to a generated initials avatar (not a stock photo) when the user has no profile image.
  const initials = encodeURIComponent((name || 'User').trim());
  return `https://ui-avatars.com/api/?name=${initials}&background=136bce&color=fff&bold=true&size=120`;
};

const mapOrgToFactory = (org: any): FactoryItem => {
  const subStatus = (org.status as 'Trial' | 'Active' | 'Grace' | 'Expired') || 'Trial';
  const operationalStatus: 'Optimal' | 'Warning' | 'Critical' =
    subStatus === 'Active' || subStatus === 'Trial' ? 'Optimal' :
    subStatus === 'Grace' ? 'Warning' : 'Critical';
  return {
    id: String(org.id),
    name: org.name,
    region: org.location_district || '',
    address: org.street_address || '',
    contactPerson: org.contact_person || undefined,
    designation: org.contact_designation || undefined,
    phoneNumber: org.contact_phone || undefined,
    inspections: 0,
    usersCount: 0,
    storageGB: 0,
    appVersion: '—',
    outdatedDevices: 0,
    status: operationalStatus,
    uptime: '—',
    enrolledDate: formatDate(org.created_at?.split('T')[0] || ''),
    allowedUsers: org.seats || 20,
    subscriptionStatus: subStatus,
    subscriptionStart: org.start_date ? ymdToReadable(org.start_date) : '',
    subscriptionEnd: org.expiry_date ? ymdToReadable(org.expiry_date) : '',
    isDisabled: false,
    isArchived: Boolean(org.is_archived),
    archivedAt: org.archived_at || null,
    allowedReportTemplates:
      Array.isArray(org.allowed_report_templates) && org.allowed_report_templates.length
        ? REPORT_TEMPLATE_KEYS.filter((k) => org.allowed_report_templates.includes(k))
        : [...DEFAULT_REPORT_TEMPLATES],
  };
};

const formatLastLogin = (lastLogin: string | null): string => {
  if (!lastLogin) return 'Never logged in';
  const d = new Date(lastLogin);
  if (isNaN(d.getTime())) return 'Never logged in';
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
};

const mapApiUserToUserItem = (user: any, factoryName: string): UserItem => ({
  id: String(user.id),
  name: user.name,
  email: user.email,
  employeeId: user.employee_id || '—',
  lastLoggedIn: formatLastLogin(user.last_login),
  inspectionsCount: user.inspections_count ?? 0,
  appVersion: user.app_version || '—',
  osVersion: user.os_version || undefined,
  deviceName: user.device_name || undefined,
  usageSeconds: user.usage_seconds ?? undefined,
  status: user.is_active ? 'Active' : 'Disabled',
  factoryName,
  role: (user.role === 'admin' || user.role === 'super_admin') ? 'Admin' : 'Staff',
  modules: [],
  profileImage: user.profileImage || user.profile_image || undefined,
});

export const FactoryManagementPanel: React.FC = () => {
  // State
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [factories, setFactories] = useState<FactoryItem[]>([]);
  const [isLoadingFactories, setIsLoadingFactories] = useState(true);
  const [factoriesError, setFactoriesError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [selectedFactoryName, setSelectedFactoryName] = useState<string>('');

  // Searches & Filters (Factory)
  const [factorySearch, setFactorySearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Trial' | 'Active' | 'Grace' | 'Expired' | 'Archived'>('All');

  // Searches & Filters (User)
  const [userSearch, setUserSearch] = useState('');
  const [userVersionFilter, setUserVersionFilter] = useState('All');

  // Add modals state
  const [showAddFactoryModal, setShowAddFactoryModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUserForDetail, setSelectedUserForDetail] = useState<UserItem | null>(null);
  const [copiedFactoryName, setCopiedFactoryName] = useState<string | null>(null);
  const [confirmFactoryState, setConfirmFactoryState] = useState<string | null>(null);
  const [copiedUserEmail, setCopiedUserEmail] = useState<string | null>(null);

  // Add User submission state
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const [userSubmitError, setUserSubmitError] = useState<string | null>(null);

  // Delete User state
  const [userPendingDelete, setUserPendingDelete] = useState<UserItem | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [deleteUserError, setDeleteUserError] = useState<string | null>(null);

  // Archive / Unarchive / Delete Factory state
  const [factoryPendingArchive, setFactoryPendingArchive] = useState<FactoryItem | null>(null);
  const [isArchivingFactory, setIsArchivingFactory] = useState(false);
  const [archiveFactoryError, setArchiveFactoryError] = useState<string | null>(null);
  const [isUnarchivingFactory, setIsUnarchivingFactory] = useState(false);
  const [factoryPendingDelete, setFactoryPendingDelete] = useState<FactoryItem | null>(null);
  const [deletePasswordModalOpen, setDeletePasswordModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deletePasswordValue, setDeletePasswordValue] = useState('');
  const [isDeletingFactory, setIsDeletingFactory] = useState(false);
  const [deleteFactoryError, setDeleteFactoryError] = useState<string | null>(null);

  // New Factory Form State
  const [isSubmittingFactory, setIsSubmittingFactory] = useState(false);
  const [factorySubmitError, setFactorySubmitError] = useState<string | null>(null);

  const [newFactory, setNewFactory] = useState({
    name: '',
    region: '',
    address: '',
    contactPerson: '',
    designation: '',
    phoneNumber: '',
    subscriptionStatus: 'Trial' as 'Trial' | 'Active' | 'Grace' | 'Expired',
    subscriptionStart: '',
    subscriptionEnd: '',
    allowedUsers: 20,
    admin_name: '',
    admin_email: '',
    admin_password: '',
  });

  // Subscription plan & contract save state
  const [isSavingSubscription, setIsSavingSubscription] = useState(false);
  const [subscriptionSaveError, setSubscriptionSaveError] = useState<string | null>(null);
  const [subscriptionSaved, setSubscriptionSaved] = useState(false);

  // Reports Template card
  const [templateDraft, setTemplateDraft] = useState<ReportTemplateKey[]>([...DEFAULT_REPORT_TEMPLATES]);
  const [isSavingTemplates, setIsSavingTemplates] = useState(false);
  const [templatesSaved, setTemplatesSaved] = useState(false);
  const [templatesSaveError, setTemplatesSaveError] = useState<string | null>(null);

  // Edit Factory State
  const [showEditFactoryModal, setShowEditFactoryModal] = useState(false);
  const [editFactoryForm, setEditFactoryForm] = useState({
    name: '',
    region: '',
    address: '',
    contactPerson: '',
    designation: '',
    phoneNumber: '',
  });

  // New User Form State
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    employeeId: '',
    password: '',
    status: 'Active' as 'Active' | 'Disabled',
    role: 'Staff' as 'Admin' | 'Staff',
  });

  const fetchOrganizations = async () => {
    setIsLoadingFactories(true);
    setFactoriesError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { ...(token && { Authorization: `Bearer ${token}` }) };
      // Pull the org list and the cockpit stats together. Stats give us the real
      // per-org inspection counts + storage usage (the org list endpoint doesn't).
      const [response, statsResponse] = await Promise.all([
        fetch(`${baseUrl}/api/v1/organizations`, { headers }),
        fetch(`${baseUrl}/api/v1/organizations/stats/cockpit`, { headers }),
      ]);
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to fetch organizations');

      // Build id -> value maps from the stats payload (best-effort; defaults to 0).
      const inspectionsByOrg = new Map<string, number>();
      const storageByOrg = new Map<string, string>();
      try {
        const stats = await statsResponse.json();
        if (statsResponse.ok && stats.success) {
          for (const e of stats.data.inspectionChart || []) {
            inspectionsByOrg.set(String(e.organizationId), e.inspections);
          }
          for (const s of stats.data.storageLeaderboard || []) {
            storageByOrg.set(String(s.organizationId), s.size);
          }
        }
      } catch { /* leave inspections/storage at their 0 defaults */ }

      const mapped: FactoryItem[] = (data.data || []).map((org: any) => {
        const factory = mapOrgToFactory(org);
        factory.inspections = inspectionsByOrg.get(String(org.id)) ?? 0;
        factory.storageLabel = storageByOrg.get(String(org.id)) ?? '0 GB';
        return factory;
      });
      setFactories(mapped);
      if (mapped.length > 0 && !selectedFactoryName) {
        setSelectedFactoryName(mapped[0].name);
      }
    } catch (err: any) {
      setFactoriesError(err.message || 'Failed to load organizations');
    } finally {
      setIsLoadingFactories(false);
    }
  };

  useEffect(() => { fetchOrganizations(); }, []);

  const fetchOrganizationUsers = async (orgId: string, factoryName: string) => {
    setIsLoadingUsers(true);
    setUsersError(null);
    setUsers([]);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/organizations/${orgId}/users`, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to fetch users');
      setUsers((data.data || []).map((u: any) => mapApiUserToUserItem(u, factoryName)));
    } catch (err: any) {
      setUsersError(err.message || 'Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Derived current factory
  const selectedFactory = factories.find(f => f.name === selectedFactoryName) || factories[0];

  useEffect(() => {
    if (selectedFactory?.id) {
      fetchOrganizationUsers(selectedFactory.id, selectedFactory.name);
    } else {
      setUsers([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFactory?.id]);

  // Reset the Reports Template draft whenever a different factory is selected.
  useEffect(() => {
    setTemplateDraft(selectedFactory?.allowedReportTemplates ?? [...DEFAULT_REPORT_TEMPLATES]);
    setTemplatesSaved(false);
    setTemplatesSaveError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFactory?.id]);

  useEffect(() => {
    if (showAddFactoryModal && newFactory.subscriptionStatus === 'Trial') {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + 15);
      const expiryStr = `${expiry.getFullYear()}-${String(expiry.getMonth() + 1).padStart(2, '0')}-${String(expiry.getDate()).padStart(2, '0')}`;

      setNewFactory(prev => ({
        ...prev,
        allowedUsers: 20,
        subscriptionStart: todayStr,
        subscriptionEnd: expiryStr
      }));
    }
  }, [showAddFactoryModal, newFactory.subscriptionStatus]);

  const [roleTab, setRoleTab] = useState<'Admin' | 'Staff'>('Staff');

  // Filter factories
  const computeFilteredFactories = (status: typeof statusFilter) => factories.filter(factory => {
    const matchesSearch = factory.name.toLowerCase().includes(factorySearch.toLowerCase()) ||
                          factory.region.toLowerCase().includes(factorySearch.toLowerCase());
    // Archived factories only show up under the dedicated "Archived" filter — every
    // other filter (including "All") hides them, since they're effectively retired.
    if (status === 'Archived') {
      return matchesSearch && Boolean(factory.isArchived);
    }
    if (factory.isArchived) return false;
    const subStatus = factory.subscriptionStatus ?? 'Active';
    const matchesStatus = status === 'All' || subStatus === status;
    return matchesSearch && matchesStatus;
  });
  const filteredFactories = computeFilteredFactories(statusFilter);

  // Filter users for selected factory
  const selectedFactoryUsers = users.filter(user => user.factoryName === selectedFactoryName);
  const filteredUsers = selectedFactoryUsers.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
                          user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
                          user.employeeId.toLowerCase().includes(userSearch.toLowerCase());
    const matchesVersion = userVersionFilter === 'All' || user.appVersion === userVersionFilter;
    const userRole = user.role || 'Staff';
    const matchesRole = userRole === roleTab;
    return matchesSearch && matchesVersion && matchesRole;
  });

  // Unique app versions for selection
  const userVersions = Array.from(new Set(selectedFactoryUsers.map(u => u.appVersion)));

  const handleCreateFactory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFactory.name || !newFactory.region || !newFactory.address) return;
    if (!newFactory.admin_name || !newFactory.admin_email || !newFactory.admin_password) return;

    setIsSubmittingFactory(true);
    setFactorySubmitError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          name: newFactory.name,
          location_district: newFactory.region,
          street_address: newFactory.address,
          contact_person: newFactory.contactPerson || undefined,
          contact_phone: newFactory.phoneNumber || undefined,
          contact_designation: newFactory.designation || undefined,
          status: newFactory.subscriptionStatus,
          seats: newFactory.allowedUsers,
          start_date: newFactory.subscriptionStart || undefined,
          expiry_date: newFactory.subscriptionEnd || undefined,
          admin_name: newFactory.admin_name,
          admin_email: newFactory.admin_email,
          admin_password: newFactory.admin_password,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to register organization');

      const createdName = newFactory.name;
      setShowAddFactoryModal(false);
      setNewFactory({
        name: '', region: '', address: '', contactPerson: '', designation: '',
        phoneNumber: '', subscriptionStatus: 'Trial', subscriptionStart: '',
        subscriptionEnd: '', allowedUsers: 20, admin_name: '', admin_email: '', admin_password: '',
      });
      await fetchOrganizations();
      setSelectedFactoryName(createdName);
    } catch (err: any) {
      setFactorySubmitError(err.message || 'Failed to register factory. Please try again.');
    } finally {
      setIsSubmittingFactory(false);
    }
  };

  const handleEditFactory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFactoryForm.name || !editFactoryForm.region || !editFactoryForm.address) return;

    const orgId = selectedFactory?.id;
    if (!orgId) { alert('Cannot edit: organization ID missing.'); return; }

    setIsSubmittingFactory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/organizations/${orgId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          name: editFactoryForm.name,
          location_district: editFactoryForm.region,
          street_address: editFactoryForm.address,
          contact_person: editFactoryForm.contactPerson || null,
          contact_phone: editFactoryForm.phoneNumber || null,
          contact_designation: editFactoryForm.designation || null,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to update organization');

      const updatedName = editFactoryForm.name;
      setShowEditFactoryModal(false);
      await fetchOrganizations();
      setSelectedFactoryName(updatedName);
    } catch (err: any) {
      alert(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setIsSubmittingFactory(false);
    }
  };

  const handleSaveSubscription = async () => {
    const orgId = selectedFactory?.id;
    if (!orgId) { setSubscriptionSaveError('Cannot save: organization ID missing.'); return; }

    setIsSavingSubscription(true);
    setSubscriptionSaveError(null);
    setSubscriptionSaved(false);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/organizations/${orgId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          status: selectedFactory.subscriptionStatus ?? 'Active',
          seats: selectedFactory.allowedUsers ?? 20,
          start_date: dateToYMD(selectedFactory.subscriptionStart ?? '') || null,
          expiry_date: dateToYMD(selectedFactory.subscriptionEnd ?? '') || null,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to update subscription');

      const savedName = selectedFactory.name;
      await fetchOrganizations();
      setSelectedFactoryName(savedName);
      setSubscriptionSaved(true);
      setTimeout(() => setSubscriptionSaved(false), 2500);
    } catch (err: any) {
      setSubscriptionSaveError(err.message || 'Failed to save subscription. Please try again.');
    } finally {
      setIsSavingSubscription(false);
    }
  };

  const toggleTemplate = (key: ReportTemplateKey) => {
    setTemplateDraft((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : REPORT_TEMPLATE_KEYS.filter((k) => k === key || prev.includes(k)),
    );
    setTemplatesSaved(false);
    setTemplatesSaveError(null);
  };

  const templatesDirty =
    JSON.stringify([...templateDraft].sort()) !==
    JSON.stringify([...(selectedFactory?.allowedReportTemplates ?? DEFAULT_REPORT_TEMPLATES)].sort());

  const handleSaveReportTemplates = async () => {
    const orgId = selectedFactory?.id;
    if (!orgId) { setTemplatesSaveError('Cannot save: organization ID missing.'); return; }
    if (templateDraft.length === 0) { setTemplatesSaveError('Select at least one template.'); return; }

    setIsSavingTemplates(true);
    setTemplatesSaveError(null);
    setTemplatesSaved(false);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/organizations/${orgId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ allowed_report_templates: templateDraft }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to update templates');

      const savedName = selectedFactory.name;
      await fetchOrganizations();
      setSelectedFactoryName(savedName);
      setTemplatesSaved(true);
      setTimeout(() => setTemplatesSaved(false), 2500);
    } catch (err: any) {
      setTemplatesSaveError(err.message || 'Failed to save templates. Please try again.');
    } finally {
      setIsSavingTemplates(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.employeeId || !newUser.password) return;

    const orgId = selectedFactory?.id;
    if (!orgId) { setUserSubmitError('No facility selected. Pick a factory first.'); return; }

    setIsSubmittingUser(true);
    setUserSubmitError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/organizations/${orgId}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          name: newUser.name,
          email: newUser.email,
          password: newUser.password,
          employee_id: newUser.employeeId,
          role: newUser.role,
          is_active: newUser.status === 'Active',
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to create user');

      setShowAddUserModal(false);
      setNewUser({
        name: '',
        email: '',
        employeeId: '',
        password: '',
        status: 'Active',
        role: 'Staff',
      });
      // Re-pull the org's users so the new account shows with its real server data.
      await fetchOrganizationUsers(orgId, selectedFactory.name);
    } catch (err: any) {
      setUserSubmitError(err.message || 'Failed to create user. Please try again.');
    } finally {
      setIsSubmittingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userPendingDelete) return;
    const orgId = selectedFactory?.id;

    setIsDeletingUser(true);
    setDeleteUserError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/users/${userPendingDelete.id}`, {
        method: 'DELETE',
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to delete user');

      setUserPendingDelete(null);
      if (orgId) {
        await fetchOrganizationUsers(orgId, selectedFactory!.name);
      } else {
        setUsers(prev => prev.filter(u => u.id !== userPendingDelete.id));
      }
    } catch (err: any) {
      setDeleteUserError(err.message || 'Failed to delete user. Please try again.');
    } finally {
      setIsDeletingUser(false);
    }
  };

  const handleArchiveFactory = async () => {
    if (!factoryPendingArchive?.id) return;
    setIsArchivingFactory(true);
    setArchiveFactoryError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/organizations/${factoryPendingArchive.id}/archive`, {
        method: 'POST',
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to archive factory');

      setFactoryPendingArchive(null);
      await fetchOrganizations();
      setStatusFilter('Archived');
    } catch (err: any) {
      setArchiveFactoryError(err.message || 'Failed to archive factory. Please try again.');
    } finally {
      setIsArchivingFactory(false);
    }
  };

  const handleUnarchiveFactory = async () => {
    if (!selectedFactory?.id) return;
    setIsUnarchivingFactory(true);
    setArchiveFactoryError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/organizations/${selectedFactory.id}/unarchive`, {
        method: 'POST',
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to unarchive factory');

      await fetchOrganizations();
      setStatusFilter('All');
    } catch (err: any) {
      setArchiveFactoryError(err.message || 'Failed to unarchive factory. Please try again.');
    } finally {
      setIsUnarchivingFactory(false);
    }
  };

  const handleDeleteFactory = async () => {
    if (!factoryPendingDelete?.id) return;
    setIsDeletingFactory(true);
    setDeleteFactoryError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/v1/organizations/${factoryPendingDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ password: deletePasswordValue }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to delete factory');

      const deletedName = factoryPendingDelete.name;
      setDeleteConfirmModalOpen(false);
      setFactoryPendingDelete(null);
      setDeletePasswordValue('');
      if (selectedFactoryName === deletedName) setSelectedFactoryName('');
      await fetchOrganizations();
    } catch (err: any) {
      // Send the admin back to the password step so they can retry (e.g. wrong password).
      setDeleteConfirmModalOpen(false);
      setDeletePasswordModalOpen(true);
      setDeleteFactoryError(err.message || 'Failed to delete factory. Please try again.');
    } finally {
      setIsDeletingFactory(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
        {/* LEFT COLUMN: Factories Directory (4 cols when open, 1 col when collapsed) */}
        <div className={`transition-all duration-300 ${
          isSidebarOpen 
            ? 'xl:col-span-4 w-full flex flex-col gap-6 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm' 
            : 'xl:col-span-1 w-full hidden xl:flex flex-col items-center gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm'
        }`}>
          {isSidebarOpen ? (
            <>
              {/* Expanded Sidebar Header */}
              <div className="flex items-center justify-between gap-4 pb-1">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Factory Directory</h2>
                  <p className="text-xs text-slate-500">Search and manage factories</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setShowAddFactoryModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-[#136bce] hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all"
                  >
                    <span className="material-symbols-outlined text-[16px]">add</span>
                    Add New
                  </button>
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    title="Collapse Directory"
                    className="w-8 h-8 rounded-xl text-slate-500 hover:text-[#136bce] hover:bg-blue-50/50 border border-slate-200 hover:border-blue-200 transition-all flex items-center justify-center bg-white shadow-xs active:scale-95"
                  >
                    <span className="material-symbols-outlined text-[18px]">keyboard_double_arrow_left</span>
                  </button>
                </div>
              </div>

              {/* Searching & Filter panel */}
              <div className="flex flex-col gap-3">
                <div className="relative">
                  <input
                    className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-4 text-xs placeholder-slate-400 focus:border-[#136bce] focus:outline-none focus:ring-1 focus:ring-[#136bce]"
                    placeholder="Search by factory name or region..."
                    type="text"
                    value={factorySearch}
                    onChange={(e) => setFactorySearch(e.target.value)}
                  />
                  <span className="material-symbols-outlined absolute left-3 top-2.5 text-[16px] text-slate-400">search</span>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">Filter status</span>
                  <div className="flex gap-1 overflow-x-auto py-1">
                    {(['All', 'Trial', 'Active', 'Grace', 'Expired', 'Archived'] as const).map((st) => (
                      <button
                        key={st}
                        onClick={() => {
                          setStatusFilter(st);
                          const nextList = computeFilteredFactories(st);
                          setSelectedFactoryName(nextList[0]?.name ?? '');
                        }}
                        className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg transition-all border whitespace-nowrap ${
                          statusFilter === st
                            ? st === 'Archived'
                              ? 'bg-rose-50 text-rose-700 border-rose-200'
                              : 'bg-blue-50 text-[#136bce] border-blue-200'
                            : 'bg-white hover:bg-slate-50 text-slate-600 border-slate-200'
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Factory Cards List */}
              <div className="space-y-3 max-h-[580px] overflow-y-auto pr-1">
                {isLoadingFactories ? (
                  <div className="py-10 text-center">
                    <div className="h-7 w-7 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent mx-auto mb-3"></div>
                    <p className="text-xs text-slate-400 font-medium">Loading organizations...</p>
                  </div>
                ) : factoriesError ? (
                  <div className="py-8 text-center border border-dashed border-red-200 rounded-xl bg-red-50/50">
                    <span className="material-symbols-outlined text-red-300 text-[32px] mb-2">error</span>
                    <p className="text-xs font-semibold text-red-500">{factoriesError}</p>
                    <button onClick={fetchOrganizations} className="mt-2 text-xs text-[#136bce] underline">Retry</button>
                  </div>
                ) : filteredFactories.length === 0 ? (
                  <div className="py-8 text-center border border-dashed border-slate-200 rounded-xl">
                    <span className="material-symbols-outlined text-slate-300 text-[36px] mb-2">domain_disabled</span>
                    <p className="text-xs font-semibold text-slate-400">No organizations yet. Add the first one.</p>
                  </div>
                ) : (
                  filteredFactories.map((factory) => {
                    const isSelected = factory.name === selectedFactoryName;
                    return (
                      <motion.div
                        key={factory.name}
                        onClick={() => setSelectedFactoryName(factory.name)}
                        className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer flex flex-col gap-3 relative overflow-hidden group ${
                          isSelected
                            ? 'bg-blue-50/60 border-[#136bce] ring-1 ring-[#136bce]/20 shadow-md translate-x-1'
                            : 'bg-white hover:bg-slate-50/80 border-slate-150 hover:border-slate-350 hover:shadow-sm'
                        }`}
                        whileTap={{ scale: 0.98 }}
                      >
                        {/* Left accent bar for highlighted item */}
                        {isSelected && (
                          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#136bce] rounded-r-md" />
                        )}

                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h4 className={`font-bold transition-colors text-sm leading-snug ${
                                isSelected ? 'text-[#136bce]' : 'text-slate-900 group-hover:text-[#136bce]'
                              }`}>{factory.name}</h4>
                              {factory.isDisabled && (
                                <span className="bg-rose-100 text-rose-850 text-[8px] font-black px-1.5 py-0.2 rounded-full border border-rose-200 uppercase tracking-wider">
                                  Disabled
                                </span>
                              )}
                              {factory.isArchived && (
                                <span className="bg-slate-800 text-white text-[8px] font-black px-1.5 py-0.2 rounded-full border border-slate-700 uppercase tracking-wider flex items-center gap-0.5">
                                  <span className="material-symbols-outlined text-[10px]">archive</span>
                                  Archived
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <span className="material-symbols-outlined text-[13px] text-slate-400">location_on</span>
                              {factory.region}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {/* Subscription Status Badge */}
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold border ${
                              (factory.subscriptionStatus ?? 'Active') === 'Trial' ? 'bg-blue-50 text-blue-700 border-blue-200/55' :
                              (factory.subscriptionStatus ?? 'Active') === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200/55' :
                              (factory.subscriptionStatus ?? 'Active') === 'Grace' ? 'bg-amber-50 text-amber-700 border-amber-200/55' :
                              'bg-rose-50 text-rose-700 border-rose-200/55'
                            }`}>
                              {(factory.subscriptionStatus ?? 'Active')}
                            </span>

                            {/* Edit Factory */}
                            <button
                              type="button"
                              title="Edit Factory"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFactoryName(factory.name);
                                setEditFactoryForm({
                                  name: factory.name,
                                  region: factory.region,
                                  address: factory.address || '',
                                  contactPerson: factory.contactPerson || '',
                                  designation: factory.designation || '',
                                  phoneNumber: factory.phoneNumber || '',
                                });
                                setShowEditFactoryModal(true);
                              }}
                              className="w-6 h-6 rounded-lg flex items-center justify-center text-slate-400 hover:text-[#136bce] hover:bg-blue-50 transition-all shrink-0"
                            >
                              <span className="material-symbols-outlined text-[15px]">edit</span>
                            </button>

                            {/* Chevron / select indicator */}
                            <span className={`material-symbols-outlined text-[18px] transition-all duration-200 ${
                              isSelected 
                                ? 'text-[#136bce] scale-110' 
                                : 'text-slate-300 opacity-40 group-hover:opacity-100 group-hover:text-[#136bce] group-hover:translate-x-0.5'
                            }`}>
                              {isSelected ? 'arrow_circle_right' : 'chevron_right'}
                            </span>
                          </div>
                        </div>
   
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100/80 text-[11px] text-slate-500">
                          <div>
                            <span className="block text-[9px] text-slate-400 uppercase font-semibold">Active Users</span>
                            <span className="font-bold text-slate-800">
                              {users.filter(u => u.factoryName === factory.name && u.status === 'Active').length}
                            </span>
                          </div>
                          <div>
                            <span className="block text-[9px] text-slate-400 uppercase font-semibold">No of Insp</span>
                            <span className="font-bold text-[#136bce]">{factory.inspections}</span>
                          </div>
                          <div>
                            <span className="block text-[9px] text-slate-400 uppercase font-semibold">Enrolled</span>
                            <span className="font-bold text-slate-700">{factory.enrolledDate}</span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              {/* Collapsed Sidebar View (Desktop only rail) */}
              <button
                onClick={() => setIsSidebarOpen(true)}
                title="Expand Directory"
                className="w-10 h-10 rounded-xl text-slate-500 hover:text-[#136bce] hover:bg-blue-50 hover:border-blue-200 bg-slate-50 border border-slate-200/80 transition-all flex items-center justify-center shadow-sm active:scale-95"
              >
                <span className="material-symbols-outlined text-[20px] font-bold">keyboard_double_arrow_right</span>
              </button>

              <div className="w-full h-[1px] bg-slate-100" />

              {/* Total Qty Indicator */}
              <div className="flex flex-col items-center justify-center text-center">
                <span className="text-[9px] uppercase font-bold text-slate-400 tracking-wider">Qty</span>
                <span className="text-xs font-extrabold text-[#136bce] bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-lg mt-0.5">
                  {filteredFactories.length}
                </span>
              </div>

              <div className="w-full h-[1px] bg-slate-100" />

              {/* Compact List of Circles */}
              <div className="flex flex-col gap-3 w-full items-center max-h-[500px] overflow-y-auto pt-1 scrollbar-none">
                {filteredFactories.map((factory) => {
                  const isSelected = factory.name === selectedFactoryName;
                  const initials = factory.name
                    ? factory.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
                    : 'F';
                  const subStatus = factory.subscriptionStatus ?? 'Active';

                  const statusDotColor = 
                    subStatus === 'Trial' ? 'bg-blue-500' :
                    subStatus === 'Active' ? 'bg-emerald-500' :
                    subStatus === 'Grace' ? 'bg-amber-500' :
                    'bg-rose-500';

                  return (
                    <button
                      key={factory.name}
                      onClick={() => setSelectedFactoryName(factory.name)}
                      className={`relative w-11 h-11 rounded-xl flex items-center justify-center font-bold text-xs transition-all duration-200 group/item border ${
                        isSelected
                          ? 'bg-[#136bce] text-white border-[#136bce] shadow-md shadow-blue-500/15 scale-105 ring-2 ring-blue-100/40'
                          : 'bg-white text-slate-600 hover:bg-blue-50 hover:text-[#136bce] border-slate-200 hover:border-blue-250'
                      }`}
                    >
                      {/* Active indicator bar */}
                      {isSelected && (
                        <div className="absolute left-0 w-1 h-5 bg-white rounded-r-full" />
                      )}

                      <span>{initials}</span>

                      {/* Tooltip on hover */}
                      <div className="absolute left-14 bg-slate-900 text-white rounded-xl p-3 text-left text-[11px] font-normal leading-normal shadow-xl border border-slate-800 opacity-0 pointer-events-none group-hover/item:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 flex flex-col gap-1 min-w-[155px]">
                        <div className="flex items-center justify-between gap-2 border-b border-slate-800 pb-1 mb-1 font-bold text-white">
                          <span className="truncate max-w-[110px]">{factory.name}</span>
                          <span className={`w-2 h-2 rounded-full ring-1 ring-white/25 ${statusDotColor}`} />
                        </div>
                        <div className="text-slate-400 text-[10px] flex items-center gap-1">
                          <span className="material-symbols-outlined text-[11px]">location_on</span>
                          {factory.region}
                        </div>
                        <div className="text-slate-400 text-[10px] flex items-center gap-1">
                          <span className="material-symbols-outlined text-[11px]">card_membership</span>
                          Status: <span className="font-extrabold text-blue-300">{subStatus}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="w-full h-[1px] bg-slate-100" />

              {/* Compact Add Factory */}
              <button
                onClick={() => setShowAddFactoryModal(true)}
                title="Add New Factory"
                className="w-10 h-10 rounded-xl text-slate-500 hover:text-white hover:bg-blue-600 hover:border-blue-600 bg-slate-50 border border-slate-200/80 hover:shadow-md hover:shadow-blue-500/10 flex items-center justify-center transition-all active:scale-95"
              >
                <span className="material-symbols-outlined text-[18px]">add</span>
              </button>
            </>
          )}
        </div>

        {/* RIGHT COLUMN: Selected Factory Details + Users Table */}
        <div className={`transition-all duration-300 ${isSidebarOpen ? 'xl:col-span-8' : 'xl:col-span-11'} flex flex-col gap-8 w-full`}>
        
        {/* FACTORY DETAILS ON TOP */}
        {selectedFactory && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col gap-6 relative overflow-hidden">
            {selectedFactory.isDisabled && (
              <div className="bg-rose-50 border-b border-rose-100 px-6 py-2.5 -mx-6 -mt-6 mb-2 flex items-center justify-between text-xs font-semibold text-rose-800">
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[16px] text-rose-500">warning</span>
                  This factory has been disabled. Real-time inspections are paused.
                </span>
                {selectedFactory.subscriptionStatus !== 'Expired' && (
                  <span className="text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200 px-2 py-0.5 rounded-md uppercase tracking-wider">Pause Active</span>
                )}
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-4 flex-wrap">
                {!isSidebarOpen && (
                  <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-50/80 hover:bg-blue-100 text-[#136bce] ring-1 ring-blue-200 rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 mr-2 shrink-0"
                    title="Expand Directory"
                  >
                    <span className="material-symbols-outlined text-[16px]">keyboard_double_arrow_right</span>
                    Expand Directory
                  </button>
                )}
                <div className="w-14 h-14 rounded-2xl bg-[#136bce]/10 text-[#136bce] flex items-center justify-center border border-blue-100">
                  <span className="material-symbols-outlined text-[32px]">factory</span>
                </div>
                <div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 className="text-xl font-bold text-slate-900 leading-tight">{selectedFactory.name}</h3>
                    {selectedFactory.isDisabled ? (
                      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                        Disabled
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        selectedFactory.status === 'Optimal' ? 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-600/20' :
                        selectedFactory.status === 'Warning' ? 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/20' :
                        'bg-red-50 text-red-800 ring-1 ring-inset ring-red-600/20'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          selectedFactory.status === 'Optimal' ? 'bg-emerald-500' :
                          selectedFactory.status === 'Warning' ? 'bg-amber-500' : 'bg-red-500'
                        }`}></span>
                        {selectedFactory.status}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5 font-medium">
                    <span className="material-symbols-outlined text-[15px] text-slate-400">location_on</span>
                    {selectedFactory.region}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <span className="text-xs text-slate-400 font-bold block text-left sm:text-right uppercase tracking-wider mb-1">Subscription Status</span>
                  <span className={`text-sm font-bold border px-3.5 py-1.5 rounded-xl block text-center capitalize ${
                    (selectedFactory.subscriptionStatus ?? 'Active') === 'Trial' ? 'bg-blue-50/70 text-blue-700 border-blue-200' :
                    (selectedFactory.subscriptionStatus ?? 'Active') === 'Active' ? 'bg-emerald-50/70 text-emerald-700 border-emerald-200' :
                    (selectedFactory.subscriptionStatus ?? 'Active') === 'Grace' ? 'bg-amber-50/70 text-amber-700 border-amber-200' :
                    (selectedFactory.subscriptionStatus ?? 'Active') === 'Expired' ? 'bg-rose-50/70 text-rose-700 border-rose-200' :
                    'bg-slate-50 text-slate-705 border-slate-200'
                  }`}>
                    {selectedFactory.subscriptionStatus ?? 'Active'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 font-bold block text-left sm:text-right uppercase tracking-wider mb-1">Registration Date</span>
                  <span className="text-sm font-bold text-slate-800 bg-slate-50 border border-slate-100 px-3.5 py-1.5 rounded-xl block text-center">
                    {selectedFactory.enrolledDate}
                  </span>
                </div>
              </div>
            </div>

            {/* Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Inspections Checked</span>
                <h5 className="text-lg font-black text-slate-900 mt-1">{selectedFactory.inspections}</h5>
                <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">↑ Enterprise Sync Active</p>
              </div>

              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Device Cloud storage</span>
                <h5 className="text-lg font-black text-[#136bce] mt-1">{selectedFactory.storageLabel ?? `${selectedFactory.storageGB} GB`}</h5>
                <p className="text-[10px] text-slate-500 font-semibold mt-0.5">PDFs & Media files</p>
              </div>

              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Active Users</span>
                <h5 className="text-lg font-black text-[#136bce] mt-1">
                  {users.filter(u => u.factoryName === selectedFactory.name && u.status === 'Active').length} / {selectedFactory.allowedUsers ?? 100}
                </h5>
                <p className="text-[10px] text-slate-500 font-semibold mt-0.5">Active / Allowed capacity</p>
              </div>

              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Outdated Devices</span>
                <h5 className={`text-lg font-black mt-1 ${selectedFactory.outdatedDevices > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                  {selectedFactory.outdatedDevices}
                </h5>
                <p className="text-[10px] text-slate-500 font-semibold mt-0.5">Devices requiring update</p>
              </div>
            </div>

            {/* Factory Contact & Profile Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-slate-50/70 border border-slate-150 text-left">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px] text-slate-400">map</span>
                  Physical Address
                </span>
                <p className="text-xs font-semibold text-slate-800 leading-relaxed">
                  {selectedFactory.address || "No address configured"}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">Contact Person</span>
                  <p className="text-xs font-bold text-slate-800 mt-0.5 truncate" title={selectedFactory.contactPerson || ""}>
                    {selectedFactory.contactPerson || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">Designation</span>
                  <p className="text-xs font-bold text-slate-800 mt-0.5 truncate" title={selectedFactory.designation || ""}>
                    {selectedFactory.designation || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">Phone Number</span>
                  <p className="text-xs font-bold text-slate-800 mt-0.5 text-slate-800 truncate" title={selectedFactory.phoneNumber || ""}>
                    {selectedFactory.phoneNumber || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp Dispatcher */}
            <div className="pt-4 border-t border-slate-150">
              <div className="bg-emerald-50/10 border border-emerald-500/10 p-4 rounded-xl flex flex-col justify-between gap-3">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-emerald-800 tracking-wider flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[16px] text-emerald-600">share</span>
                      WhatsApp Dispatcher
                    </span>
                    <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                      Share active factory configuration including node credentials, URLs, and play store installation guides directly with local managers or tablet setup specialists.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    <button
                      onClick={() => {
                        const fUrl = `https://inspect.qamate.com/node/${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                        const pUrl = "https://play.google.com/store/apps/details?id=com.qamate.inspect";
                        
                        const adminUser = users.find(u => u.factoryName === selectedFactory.name && u.role === "Admin");
                        const adminEmail = adminUser?.email || `admin.${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '')}@qamate.com`;
                        const adminPassword = (adminUser as any)?.password || `Admin@${selectedFactory.name.replace(/[^a-zA-Z0-9]/g, '')}2026`;
                        
                        const msgText = `🏢 *QA-Mate Inspect Pro - Factory Node Board*\n\n` +
                          `• *Factory Name:* ${selectedFactory.name}\n` +
                          `• *Play Store Link:* ${pUrl}\n` +
                          `• *Factory URL:* ${fUrl}\n` +
                          `• *Admin Email:* ${adminEmail}\n` +
                          `• *Admin Password:* ${adminPassword}`;

                        navigator.clipboard.writeText(msgText);
                        setCopiedFactoryName("whatsapp_copy");
                        setTimeout(() => setCopiedFactoryName(null), 2000);
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-slate-100 hover:bg-slate-250 text-slate-700 font-bold rounded-xl text-xs transition-all shadow-sm active:scale-95 border border-slate-200"
                    >
                      <span className="material-symbols-outlined text-[15px]">content_copy</span>
                      {copiedFactoryName === "whatsapp_copy" ? "Copied!" : "Copy Details"}
                    </button>

                    <button
                      onClick={() => {
                        const fUrl = `https://inspect.qamate.com/node/${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                        const pUrl = "https://play.google.com/store/apps/details?id=com.qamate.inspect";
                        
                        const adminUser = users.find(u => u.factoryName === selectedFactory.name && u.role === "Admin");
                        const adminEmail = adminUser?.email || `admin.${selectedFactory.name.toLowerCase().replace(/[^a-z0-9]+/g, '')}@qamate.com`;
                        const adminPassword = (adminUser as any)?.password || `Admin@${selectedFactory.name.replace(/[^a-zA-Z0-9]/g, '')}2026`;
                        
                        const msgText = `🏢 *QA-Mate Inspect Pro - Factory Node Board*\n\n` +
                          `• *Factory Name:* ${selectedFactory.name}\n` +
                          `• *Play Store Link:* ${pUrl}\n` +
                          `• *Factory URL:* ${fUrl}\n` +
                          `• *Admin Email:* ${adminEmail}\n` +
                          `• *Admin Password:* ${adminPassword}`;

                        navigator.clipboard.writeText(msgText);
                        const wUrl = `https://wa.me/?text=${encodeURIComponent(msgText)}`;
                        window.open(wUrl, '_blank');
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition-all shadow-sm active:scale-95"
                    >
                      <span className="material-symbols-outlined text-[15px]">send</span>
                      Send via Whatsapp
                    </button>
                  </div>
                </div>
              </div>
            {/* Subscription details & Option to disable Factory */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-150">
              <div className="flex flex-col gap-2 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[16px] text-[#136bce]">workspace_premium</span>
                  Subscription plan & contract
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1 text-[11px]">
                  <div>
                    <span className="block text-[9px] text-slate-400 uppercase font-bold mb-1">Status</span>
                    <select
                      value={selectedFactory.subscriptionStatus ?? 'Active'}
                      onChange={(e) => {
                        const val = e.target.value as 'Trial' | 'Active' | 'Grace' | 'Expired';
                        setFactories(factories.map(f => {
                          if (f.name === selectedFactory.name) {
                            return { ...f, subscriptionStatus: val };
                          }
                          return f;
                        }));
                      }}
                      className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs cursor-pointer"
                    >
                      <option value="Trial">Trial</option>
                      <option value="Active">Active</option>
                      <option value="Grace">Grace</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>
                  <div>
                    <span className="block text-[9px] text-slate-400 uppercase font-bold mb-1">Start Date</span>
                    <input
                      type="date"
                      value={dateToYMD(selectedFactory.subscriptionStart ?? selectedFactory.enrolledDate)}
                      onChange={(e) => {
                        const val = ymdToReadable(e.target.value);
                        setFactories(factories.map(f => {
                          if (f.name === selectedFactory.name) {
                            return { ...f, subscriptionStart: val };
                          }
                          return f;
                        }));
                      }}
                      className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs"
                    />
                  </div>
                  <div>
                    <span className="block text-[9px] text-[#136bce] sm:text-slate-400 uppercase font-bold mb-1">Expiry Date</span>
                    <input
                      type="date"
                      value={dateToYMD(selectedFactory.subscriptionEnd ?? '15 Jan 2027')}
                      onChange={(e) => {
                        const val = ymdToReadable(e.target.value);
                        setFactories(factories.map(f => {
                          if (f.name === selectedFactory.name) {
                            return { ...f, subscriptionEnd: val };
                          }
                          return f;
                        }));
                      }}
                      className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs"
                    />
                  </div>
                  <div>
                    <span className="block text-[9px] text-slate-400 uppercase font-bold mb-1">Allowed Seats</span>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={selectedFactory.allowedUsers ?? 100}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10) || 100;
                        setFactories(factories.map(f => {
                          if (f.name === selectedFactory.name) {
                            return { ...f, allowedUsers: val };
                          }
                          return f;
                        }));
                      }}
                      className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs"
                    />
                  </div>
                </div>

                {/* Save subscription changes */}
                <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-150">
                  <span className="text-[10px] font-medium text-slate-500 leading-tight">
                    {subscriptionSaveError ? (
                      <span className="text-rose-600 font-semibold flex items-center gap-1">
                        <span className="material-symbols-outlined text-[13px]">error</span>
                        {subscriptionSaveError}
                      </span>
                    ) : subscriptionSaved ? (
                      <span className="text-emerald-600 font-semibold flex items-center gap-1">
                        <span className="material-symbols-outlined text-[13px]">check_circle</span>
                        Subscription updated.
                      </span>
                    ) : (
                      'Changes apply to plan status, dates & allowed seats.'
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={handleSaveSubscription}
                    disabled={isSavingSubscription}
                    className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black bg-[#136bce] hover:bg-blue-700 text-white shadow-sm transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSavingSubscription ? (
                      <>
                        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-[15px]">save</span>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-col justify-between bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                <div className="flex items-center justify-between gap-4 h-full">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[16px] text-slate-500">do_not_disturb_on</span>
                      Disable / Deactivate Factory
                    </span>
                    <p className="text-[10px] text-slate-500 mt-1 font-medium leading-relaxed">
                      {selectedFactory.isDisabled
                        ? "Deactivated: Tablet devices are locked, active inspection runs are suspended, and sync permissions are revoked."
                        : "Operational: Allowed to perform real-time checklists. Disabling will halt active inspector sessions instantly."
                      }
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmFactoryState(selectedFactory.name)}
                    className={`shrink-0 px-4 py-2 rounded-xl text-xs font-black transition-all active:scale-95 border shadow-sm ${
                      selectedFactory.isDisabled
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                        : 'bg-rose-50 border-rose-150 text-rose-600 hover:bg-rose-100'
                    }`}
                  >
                    {selectedFactory.isDisabled ? 'Enable Factory' : 'Disable Factory'}
                  </button>
                </div>
              </div>
            </div>

            {/* Archive / Unarchive / Permanently Delete Factory */}
            <div className={`mt-4 p-4 rounded-xl border ${
              selectedFactory.isArchived ? 'bg-rose-50/70 border-rose-200' : 'bg-slate-50/50 border-slate-100'
            }`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[16px] text-rose-500">archive</span>
                    {selectedFactory.isArchived ? 'Factory Archived' : 'Archive Factory'}
                  </span>
                  <p className="text-[10px] text-slate-500 mt-1 font-medium leading-relaxed max-w-md">
                    {selectedFactory.isArchived
                      ? 'Login is blocked for every user at this factory (admin included). All data is untouched — unarchive to restore access, or permanently delete.'
                      : 'Archiving immediately blocks login for every user of this factory (admin included). All data is preserved and this can be undone.'}
                  </p>
                  {archiveFactoryError && (
                    <p className="text-[10px] text-rose-600 font-semibold mt-1.5 flex items-center gap-1">
                      <span className="material-symbols-outlined text-[12px]">error</span>
                      {archiveFactoryError}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {selectedFactory.isArchived ? (
                    <>
                      <button
                        type="button"
                        onClick={handleUnarchiveFactory}
                        disabled={isUnarchivingFactory}
                        className="px-4 py-2 rounded-xl text-xs font-black bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-all active:scale-95 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isUnarchivingFactory ? 'Restoring...' : 'Unarchive Factory'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteFactoryError(null);
                          setDeletePasswordValue('');
                          setFactoryPendingDelete(selectedFactory);
                          setDeletePasswordModalOpen(true);
                        }}
                        className="px-4 py-2 rounded-xl text-xs font-black bg-rose-600 hover:bg-rose-700 text-white transition-all active:scale-95 shadow-sm"
                      >
                        Delete Factory Permanently
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setArchiveFactoryError(null); setFactoryPendingArchive(selectedFactory); }}
                      className="px-4 py-2 rounded-xl text-xs font-black bg-rose-50 border border-rose-200 text-rose-600 hover:bg-rose-100 transition-all active:scale-95 shadow-sm"
                    >
                      Archive Factory
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* REPORTS TEMPLATE CARD */}
        {selectedFactory && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <span className="material-symbols-outlined text-[20px] text-[#136bce]">description</span>
                  Reports Template
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Templates this factory can use when generating inspection report PDFs.
                </p>
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto">
                {templatesSaveError && (
                  <span className="text-[11px] font-semibold text-rose-600 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">error</span>
                    {templatesSaveError}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleSaveReportTemplates}
                  disabled={isSavingTemplates || !templatesDirty || templateDraft.length === 0}
                  className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black bg-[#136bce] hover:bg-blue-700 text-white shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingTemplates ? (
                    <>
                      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                      Saving...
                    </>
                  ) : templatesSaved ? (
                    <>
                      <span className="material-symbols-outlined text-[15px]">check_circle</span>
                      Saved
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-[15px]">save</span>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {REPORT_TEMPLATE_KEYS.map((key) => {
                const meta = TEMPLATE_META[key];
                const checked = templateDraft.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleTemplate(key)}
                    className={`text-left rounded-xl border p-4 transition-all active:scale-[0.99] ${
                      checked
                        ? 'border-[#136bce] bg-blue-50 ring-1 ring-inset ring-[#136bce]/20'
                        : 'border-slate-200 bg-slate-50/50 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`h-8 w-8 shrink-0 rounded-lg ${meta.iconBg} flex items-center justify-center`}>
                        <span className={`material-symbols-outlined text-[18px] ${meta.iconColor}`}>{meta.icon}</span>
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-bold text-slate-800 truncate">{meta.name}</span>
                          <span
                            className={`material-symbols-outlined text-[18px] shrink-0 ${
                              checked ? 'text-[#136bce]' : 'text-slate-300'
                            }`}
                          >
                            {checked ? 'check_box' : 'check_box_outline_blank'}
                          </span>
                        </div>
                        {meta.finalOnly && (
                          <span className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                            Final Inspection only
                          </span>
                        )}
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">{meta.note}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {templateDraft.length === 0 && (
              <p className="text-[11px] font-semibold text-rose-600 mt-3">Select at least one template.</p>
            )}
          </div>
        )}

        {/* USERS LIST BOARD */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col gap-6 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Enrolled QA Staff ({filteredUsers.length})</h3>
              <p className="text-xs text-slate-500 mt-0.5">Tablet devices operating at this factory node</p>
            </div>

            <div className="flex items-center gap-3 self-end sm:self-center">
              <button
                onClick={() => { setUserSubmitError(null); setShowAddUserModal(true); }}
                className="flex items-center gap-1.5 px-4 py-2.5 bg-[#136bce] hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all whitespace-nowrap"
              >
                <span className="material-symbols-outlined text-[18px]">person_add</span>
                Add New User
              </button>
            </div>
          </div>

          {/* Tabs for Admin and Staff role selection */}
          <div className="flex border-b border-slate-200 -mx-6 px-6 -mt-2">
            <button
              type="button"
              onClick={() => setRoleTab('Staff')}
              className={`flex items-center gap-2 px-5 py-3 text-xs font-bold border-b-2 transition-all ${
                roleTab === 'Staff'
                  ? 'border-[#136bce] text-[#136bce] bg-blue-50/10'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">badge</span>
              Staff / Inspectors ({selectedFactoryUsers.filter(u => (u.role || 'Staff') === 'Staff').length})
            </button>
            <button
              type="button"
              onClick={() => setRoleTab('Admin')}
              className={`flex items-center gap-2 px-5 py-3 text-xs font-bold border-b-2 transition-all ${
                roleTab === 'Admin'
                  ? 'border-[#136bce] text-[#136bce] bg-blue-50/10'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">admin_panel_settings</span>
              Admin / Leads ({selectedFactoryUsers.filter(u => u.role === 'Admin').length})
            </button>
          </div>

          {/* User Search & Version Filter Panel */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
            <div className="relative flex-1">
              <input
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-4 text-xs placeholder-slate-400 focus:border-[#136bce] focus:outline-none focus:ring-1 focus:ring-[#136bce] bg-white"
                placeholder="Search QA team members by name, email or EMP ID..."
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
              <span className="material-symbols-outlined absolute left-3 top-2.5 text-[18px] text-slate-400">search</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">App Version:</span>
              <select
                className="rounded-lg border border-slate-200 bg-white py-1.5 pl-3 pr-8 text-xs focus:border-[#136bce] focus:outline-none text-slate-700 font-semibold cursor-pointer shadow-xs"
                value={userVersionFilter}
                onChange={(e) => setUserVersionFilter(e.target.value)}
              >
                <option value="All">All Versions</option>
                {userVersions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* User List Table */}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm text-slate-600 min-w-[700px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase font-bold tracking-wider text-slate-500">
                <tr>
                   <th className="px-6 py-4">User Name & Contact</th>
                   <th className="px-6 py-4">Last Logged In</th>
                   <th className="px-6 py-4 text-center">No. of Inspections</th>
                   <th className="px-6 py-4">App Version</th>
                   <th className="px-6 py-4 text-center">Status</th>
                   <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoadingUsers ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-400">
                      <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent mx-auto mb-3"></div>
                      <span className="text-xs font-medium">Loading enrolled staff...</span>
                    </td>
                  </tr>
                ) : usersError ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center">
                      <span className="material-symbols-outlined text-[32px] block text-red-300 mb-2">error</span>
                      <p className="text-xs font-semibold text-red-500">{usersError}</p>
                      <button
                        onClick={() => selectedFactory?.id && fetchOrganizationUsers(selectedFactory.id, selectedFactory.name)}
                        className="mt-2 text-xs text-[#136bce] underline"
                      >Retry</button>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-400">
                      <span className="material-symbols-outlined text-[36px] block text-slate-300 mb-2">supervisor_account</span>
                      No enrolled inspectors found matching current search/filter.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr 
                      key={user.id} 
                      onClick={() => setSelectedUserForDetail(user)}
                      className="hover:bg-blue-50/40 transition-colors cursor-pointer group"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={getUserAvatarUrl(user.name, user.profileImage)}
                            alt={user.name}
                            referrerPolicy="no-referrer"
                            className="w-9 h-9 rounded-full object-cover border border-slate-200 shadow-sm animate-fade-in"
                          />
                          <div>
                            <p className="font-bold text-slate-900 leading-tight">{user.name}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              <p className="text-[11px] text-slate-500">{user.email}</p>
                              <button
                                type="button"
                                title={copiedUserEmail === user.id ? 'Copied!' : 'Copy email'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(user.email);
                                  setCopiedUserEmail(user.id);
                                  setTimeout(() => setCopiedUserEmail(null), 2000);
                                }}
                                className="flex items-center justify-center p-0.5 text-slate-400 hover:text-[#136bce] hover:bg-blue-50 rounded transition-all active:scale-95"
                              >
                                <span className={`material-symbols-outlined text-[13px] ${copiedUserEmail === user.id ? 'text-emerald-500' : ''}`}>
                                  {copiedUserEmail === user.id ? 'check' : 'content_copy'}
                                </span>
                              </button>
                            </div>
                            <p className="text-[10px] text-slate-400 font-mono mt-1 flex items-center gap-1 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded w-max">
                              <span className="material-symbols-outlined text-[12px] text-slate-400">fingerprint</span>
                              Staff ID: <span className="font-bold text-slate-700">{user.employeeId}</span>
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-500 font-medium">
                        {user.lastLoggedIn}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-mono font-bold text-slate-900 bg-slate-100/70 px-2 py-1 rounded text-xs border border-slate-200/50">
                          {user.inspectionsCount}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const baseline = selectedFactory?.appVersion;
                          const hasBaseline = baseline && baseline !== '—';
                          const isUnknown = !user.appVersion || user.appVersion === '—';
                          // Only flag as outdated (red) when we have a factory baseline to
                          // compare against and the user's version genuinely differs.
                          const isOutdated = hasBaseline && !isUnknown && user.appVersion !== baseline;
                          return (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                              isUnknown
                                ? 'bg-slate-50 text-slate-500 border border-slate-200'
                                : isOutdated
                                  ? 'bg-red-50 text-red-700 border border-red-100 animate-pulse-subtle'
                                  : 'bg-blue-50 text-blue-700 border border-blue-100'
                            }`}>
                              {user.appVersion}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                          user.status === 'Active' 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                            : 'bg-slate-100 text-slate-500 border border-slate-200'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${user.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          type="button"
                          title="Delete user"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteUserError(null);
                            setUserPendingDelete(user);
                          }}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition-all active:scale-95"
                        >
                          <span className="material-symbols-outlined text-[18px]">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

      {/* ADDFONT DIALOGS / MODALS: ADD FACTORY MODAL */}
      <AnimatePresence>
        {showAddFactoryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-6 shadow-xl flex flex-col gap-5 overflow-y-auto max-h-[90dvh]"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#136bce]">factory</span>
                  <h3 className="text-lg font-bold text-slate-900">Add New Factory</h3>
                </div>
                <button
                  onClick={() => setShowAddFactoryModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleCreateFactory} className="flex flex-col gap-5">
                {/* SECTION 1: FACILITY PROFILE */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">info</span>
                    Facility Profile
                  </span>
                  
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Factory Name *</label>
                    <input
                      required
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="e.g. Apex Apparel Tech (Facility C)"
                      type="text"
                      value={newFactory.name}
                      onChange={(e) => setNewFactory({ ...newFactory, name: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Location / District *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. Vietnam (HCMC)"
                        type="text"
                        value={newFactory.region}
                        onChange={(e) => setNewFactory({ ...newFactory, region: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Street Address *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. Plot No. 12, Ind Area, NY"
                        type="text"
                        value={newFactory.address}
                        onChange={(e) => setNewFactory({ ...newFactory, address: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* SECTION 2: REPRESENTATIVE CONTACT INFO */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">person</span>
                    Contact Representative
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Contact Person (Optional)</label>
                      <input
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. Mr. Shanmugam"
                        type="text"
                        value={newFactory.contactPerson}
                        onChange={(e) => setNewFactory({ ...newFactory, contactPerson: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Phone Number (Optional)</label>
                      <input
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-900 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. +91 94450 12345"
                        type="text"
                        value={newFactory.phoneNumber}
                        onChange={(e) => setNewFactory({ ...newFactory, phoneNumber: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Designation / Role (Optional)</label>
                    <input
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-900 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="e.g. Plant Manager / Lead Inspector"
                      type="text"
                      value={newFactory.designation}
                      onChange={(e) => setNewFactory({ ...newFactory, designation: e.target.value })}
                    />
                  </div>
                </div>

                {/* SECTION 3: FIRST ADMIN ACCOUNT */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">admin_panel_settings</span>
                    First Admin Account <span className="text-red-500 ml-1">*</span>
                  </span>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Admin Full Name *</label>
                    <input
                      required
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="e.g. Shanmugam"
                      type="text"
                      value={newFactory.admin_name}
                      onChange={(e) => setNewFactory({ ...newFactory, admin_name: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Admin Email *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. admin@factory.com"
                        type="email"
                        value={newFactory.admin_email}
                        onChange={(e) => setNewFactory({ ...newFactory, admin_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Admin Password *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="Min 8 characters"
                        type="password"
                        value={newFactory.admin_password}
                        onChange={(e) => setNewFactory({ ...newFactory, admin_password: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* SUBSCRIPTION PLAN & CONTRACT */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col gap-3 text-left">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-200/50 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">workspace_premium</span>
                    Subscription Plan & Contract
                  </span>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Status</label>
                      <select
                        value={newFactory.subscriptionStatus}
                        onChange={(e) => setNewFactory({ ...newFactory, subscriptionStatus: e.target.value as any })}
                        className="w-full h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] cursor-pointer"
                      >
                        <option value="Trial">Trial</option>
                        <option value="Active">Active</option>
                        <option value="Grace">Grace</option>
                        <option value="Expired">Expired</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Seats</label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        value={newFactory.allowedUsers}
                        onChange={(e) => setNewFactory({ ...newFactory, allowedUsers: parseInt(e.target.value, 10) || 1 }) }
                        className="w-full h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce]"
                      />
                    </div>

                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Start Date</label>
                      <input
                        type="date"
                        value={newFactory.subscriptionStart}
                        onChange={(e) => setNewFactory({ ...newFactory, subscriptionStart: e.target.value })}
                        className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs cursor-pointer"
                      />
                    </div>

                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Expiry Date</label>
                      <input
                        type="date"
                        value={newFactory.subscriptionEnd}
                        onChange={(e) => setNewFactory({ ...newFactory, subscriptionEnd: e.target.value })}
                        className="bg-white border border-slate-200 rounded-lg h-9 px-2.5 text-xs font-bold text-slate-800 w-full focus:outline-none focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] shadow-xs cursor-pointer"
                      />
                    </div>
                  </div>
                </div>

                {factorySubmitError && (
                  <div className="rounded-xl bg-red-50 border border-red-200 p-3 flex items-center gap-2 text-red-700 text-xs font-medium">
                    <span className="material-symbols-outlined text-[16px]">error</span>
                    {factorySubmitError}
                  </div>
                )}

                <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => { setShowAddFactoryModal(false); setFactorySubmitError(null); }}
                    className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl text-xs font-bold transition-all"
                    disabled={isSubmittingFactory}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingFactory}
                    className="inline-flex items-center gap-2 px-5 py-2 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/20 disabled:opacity-70"
                  >
                    {isSubmittingFactory ? (
                      <>
                        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                        Registering...
                      </>
                    ) : 'Register Factory'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* EDIT FACTORY MODAL */}
      <AnimatePresence>
        {showEditFactoryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm shadow-2xl">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-6 shadow-xl flex flex-col gap-5 overflow-y-auto max-h-[90dvh]"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#136bce]">edit_note</span>
                  <h3 className="text-lg font-bold text-slate-900">Edit Factory Profile</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowEditFactoryModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleEditFactory} className="flex flex-col gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono">Factory Name *</label>
                  <input
                    required
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                    placeholder="e.g. Apex Apparel Tech (Facility C)"
                    type="text"
                    value={editFactoryForm.name}
                    onChange={(e) => setEditFactoryForm({ ...editFactoryForm, name: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono">Location / District *</label>
                    <input
                      required
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                      placeholder="e.g. Vietnam (HCMC)"
                      type="text"
                      value={editFactoryForm.region}
                      onChange={(e) => setEditFactoryForm({ ...editFactoryForm, region: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono font-medium text-slate-500">Contact Person (Optional)</label>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-955 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                      placeholder="e.g. Mr. Shanmugam"
                      type="text"
                      value={editFactoryForm.contactPerson}
                      onChange={(e) => setEditFactoryForm({ ...editFactoryForm, contactPerson: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono">Address *</label>
                  <textarea
                    required
                    rows={2}
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none resize-none font-sans"
                    placeholder="e.g. Plot No. 12, Ind Area, NH-7 Bypass"
                    value={editFactoryForm.address}
                    onChange={(e) => setEditFactoryForm({ ...editFactoryForm, address: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono font-medium text-slate-500">Designation (Optional)</label>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-900 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                      placeholder="e.g. Plant Manager"
                      type="text"
                      value={editFactoryForm.designation}
                      onChange={(e) => setEditFactoryForm({ ...editFactoryForm, designation: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-widest mb-1.5 font-mono font-medium text-slate-500">Phone Number (Optional)</label>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs placeholder-slate-400 text-slate-900 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none font-sans"
                      placeholder="e.g. +91 94450 12345"
                      type="text"
                      value={editFactoryForm.phoneNumber}
                      onChange={(e) => setEditFactoryForm({ ...editFactoryForm, phoneNumber: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t border-slate-100 font-sans">
                  <button
                    type="button"
                    onClick={() => setShowEditFactoryModal(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl text-xs font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/20"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* DISABLE / ENABLE FACTORY CONFIRMATION MODAL */}
      <AnimatePresence>
        {confirmFactoryState && (() => {
          const factoryObj = factories.find(f => f.name === confirmFactoryState);
          if (!factoryObj) return null;
          const isCurrentlyDisabled = factoryObj.isDisabled;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl border border-slate-200 w-full max-w-md p-6 shadow-xl flex flex-col gap-5 text-left"
              >
                <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isCurrentlyDisabled ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                  }`}>
                    <span className="material-symbols-outlined text-[20px]">
                      {isCurrentlyDisabled ? 'power' : 'dangerous'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      {isCurrentlyDisabled ? 'Enable Factory Node?' : 'Disable Factory Node?'}
                    </h3>
                    <p className="text-[11px] text-slate-500">Security Clearance Required</p>
                  </div>
                </div>

                <div className="text-xs text-slate-600 leading-relaxed space-y-2 font-sans">
                  <p>
                    You are changing the operational authorization for <span className="font-bold text-slate-800">{factoryObj.name}</span> located in <span className="font-bold text-slate-800">{factoryObj.region}</span>.
                  </p>
                  {isCurrentlyDisabled ? (
                    <p className="bg-emerald-50/50 border border-emerald-100 text-emerald-800 p-3 rounded-xl">
                      <strong>Note:</strong> Enabling this factory will immediately restore inspection access for all enrolled tablet devices. Inspectors can start syncing batch logs.
                    </p>
                  ) : (
                    <p className="bg-rose-50/50 border border-rose-150 text-rose-800 p-3 rounded-xl font-medium">
                      <strong>Warning:</strong> Disabling this factory will suspend all active inspections. Terminals will NOT be permitted to start new checklists, upload media, or sync audits until re-enabled.
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setConfirmFactoryState(null)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Toggle isDisabled status
                      setFactories(factories.map(f => {
                        if (f.name === factoryObj.name) {
                          return { ...f, isDisabled: !f.isDisabled };
                        }
                        return f;
                      }));
                      setConfirmFactoryState(null);
                    }}
                    className={`px-4 py-2 text-white rounded-xl text-xs font-bold transition-all shadow-sm ${
                      isCurrentlyDisabled
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-rose-600 hover:bg-rose-700'
                    }`}
                  >
                    {isCurrentlyDisabled ? 'Confirm Enable' : 'Confirm Disable'}
                  </button>
                </div>
              </motion.div>
            </div>
          );
        })()}
      </AnimatePresence>

      {/* ADD USER MODAL */}
      <AnimatePresence>
        {showAddUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-6 shadow-xl flex flex-col gap-5"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#136bce]">person_add</span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Add New User</h3>
                    <p className="text-[11px] text-slate-500">Create a new user account for: {selectedFactoryName}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddUserModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="flex flex-col gap-5">
                {/* SECTION 1: USER PROFILE */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">badge</span>
                    User Profile
                  </span>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Full Name *</label>
                    <input
                      required
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="e.g. Sarah Jenkins"
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Email ID *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. sarah.jenkins@qamate.com"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Employee ID *</label>
                      <input
                        required
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                        placeholder="e.g. EMP-2023-045"
                        type="text"
                        value={newUser.employeeId}
                        onChange={(e) => setNewUser({ ...newUser, employeeId: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Password *</label>
                    <input
                      required
                      className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                      placeholder="Set a login password for this user"
                      type="text"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Shared with the user for app login. They can change it later.</p>
                  </div>
                </div>

                {/* SECTION 2: REPRESENTATIVE CONFIG */}
                <div className="flex flex-col gap-3">
                  <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                    <span className="material-symbols-outlined text-[14px] text-[#136bce]">admin_panel_settings</span>
                    Role & Status
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">USER ROLE *</label>
                      <div className="grid grid-cols-2 gap-2 font-semibold">
                        <button
                          type="button"
                          onClick={() => setNewUser({ ...newUser, role: 'Staff' })}
                          className={`flex items-center justify-center gap-1.5 h-10 rounded-xl border text-xs font-bold transition-all ${
                            newUser.role === 'Staff'
                              ? 'border-[#136bce] bg-blue-50/20 text-[#136bce] ring-1 ring-[#136bce]'
                              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <span className="material-symbols-outlined text-[14px]">badge</span>
                          Staff
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewUser({ ...newUser, role: 'Admin' })}
                          className={`flex items-center justify-center gap-1.5 h-10 rounded-xl border text-xs font-bold transition-all ${
                            newUser.role === 'Admin'
                              ? 'border-[#136bce] bg-blue-50/20 text-[#136bce] ring-1 ring-[#136bce]'
                              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <span className="material-symbols-outlined text-[14px]">admin_panel_settings</span>
                          Admin
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Employee Status</label>
                      <select
                        className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs text-slate-800 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none cursor-pointer"
                        value={newUser.status}
                        onChange={(e) => setNewUser({ ...newUser, status: e.target.value as any })}
                      >
                        <option value="Active">Active</option>
                        <option value="Disabled">Disabled</option>
                      </select>
                    </div>
                  </div>
                </div>

                {userSubmitError && (
                  <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl px-3 py-2.5">
                    <span className="material-symbols-outlined text-[16px]">error</span>
                    {userSubmitError}
                  </div>
                )}

                <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    disabled={isSubmittingUser}
                    onClick={() => setShowAddUserModal(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl text-xs font-bold transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingUser}
                    className="px-5 py-2 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/20 disabled:opacity-60 flex items-center gap-2"
                  >
                    {isSubmittingUser && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent"></span>}
                    {isSubmittingUser ? 'Adding...' : 'Add User'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* DELETE USER CONFIRMATION MODAL */}
      {userPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-slate-900 bg-opacity-75 backdrop-blur-sm transition-opacity"
            onClick={() => !isDeletingUser && setUserPendingDelete(null)}
          ></div>
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>
          <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-50/50">
                <span className="material-symbols-outlined text-[32px]">warning</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Delete User?</h3>
              <p className="text-sm text-slate-500 mb-8">
                Are you sure you want to delete <span className="font-semibold text-slate-700">{userPendingDelete.name}</span> (<span className="font-semibold text-slate-700">{userPendingDelete.email}</span>)? This action is permanent, deletes all associated data, and cannot be undone.
              </p>
              {deleteUserError && (
                <div className="flex items-center justify-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm font-semibold rounded-lg px-3 py-2.5 mb-6">
                  <span className="material-symbols-outlined text-[18px]">error</span>
                  {deleteUserError}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setUserPendingDelete(null)}
                  disabled={isDeletingUser}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={isDeletingUser}
                  className="rounded-lg bg-red-600 px-6 py-3 text-sm font-bold text-white hover:bg-red-700 shadow-md shadow-red-200 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  type="button"
                  style={{ flexGrow: 2 }}
                >
                  {isDeletingUser ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>Confirm Delete</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ARCHIVE FACTORY CONFIRMATION MODAL */}
      {factoryPendingArchive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-slate-900 bg-opacity-75 backdrop-blur-sm transition-opacity"
            onClick={() => !isArchivingFactory && setFactoryPendingArchive(null)}
          ></div>
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>
          <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-4 ring-rose-50/50">
                <span className="material-symbols-outlined text-[32px]">archive</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Archive Factory?</h3>
              <p className="text-sm text-slate-500 mb-8">
                Archiving <span className="font-semibold text-slate-700">{factoryPendingArchive.name}</span> will immediately block login for every user at this factory, admins included. All data is preserved and this can be undone from the Archived filter.
              </p>
              {archiveFactoryError && (
                <div className="flex items-center justify-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm font-semibold rounded-lg px-3 py-2.5 mb-6">
                  <span className="material-symbols-outlined text-[18px]">error</span>
                  {archiveFactoryError}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setFactoryPendingArchive(null)}
                  disabled={isArchivingFactory}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleArchiveFactory}
                  disabled={isArchivingFactory}
                  className="rounded-lg bg-rose-600 px-6 py-3 text-sm font-bold text-white hover:bg-rose-700 shadow-sm transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  type="button"
                  style={{ flexGrow: 2 }}
                >
                  {isArchivingFactory ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                      Archiving...
                    </>
                  ) : (
                    <>Confirm Archive</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE FACTORY - STEP 1: PASSWORD CONFIRMATION MODAL */}
      {deletePasswordModalOpen && factoryPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-slate-900 bg-opacity-75 backdrop-blur-sm transition-opacity"
            onClick={() => { setDeletePasswordModalOpen(false); setFactoryPendingDelete(null); setDeletePasswordValue(''); setDeleteFactoryError(null); }}
          ></div>
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>
          <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!deletePasswordValue) return;
                setDeleteFactoryError(null);
                setDeletePasswordModalOpen(false);
                setDeleteConfirmModalOpen(true);
              }}
              className="p-8 text-center"
            >
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-4 ring-rose-50/50">
                <span className="material-symbols-outlined text-[32px]">lock</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Confirm Your Password</h3>
              <p className="text-sm text-slate-500 mb-6">
                You're about to permanently delete <span className="font-semibold text-slate-700">{factoryPendingDelete.name}</span>. Re-enter your super admin password to continue.
              </p>
              <input
                type="password"
                autoFocus
                value={deletePasswordValue}
                onChange={(e) => setDeletePasswordValue(e.target.value)}
                placeholder="Your login password"
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm mb-2 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
              />
              {deleteFactoryError && (
                <div className="flex items-center justify-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm font-semibold rounded-lg px-3 py-2.5 mt-4">
                  <span className="material-symbols-outlined text-[18px]">error</span>
                  {deleteFactoryError}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setDeletePasswordModalOpen(false); setFactoryPendingDelete(null); setDeletePasswordValue(''); setDeleteFactoryError(null); }}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!deletePasswordValue}
                  className="rounded-lg bg-rose-600 px-6 py-3 text-sm font-bold text-white hover:bg-rose-700 shadow-sm transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ flexGrow: 2 }}
                >
                  Continue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE FACTORY - STEP 2: IRREVERSIBLE ACTION RECONFIRM MODAL */}
      {deleteConfirmModalOpen && factoryPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-slate-900 bg-opacity-75 backdrop-blur-sm transition-opacity"
            onClick={() => !isDeletingFactory && setDeleteConfirmModalOpen(false)}
          ></div>
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>
          <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-50/50">
                <span className="material-symbols-outlined text-[32px]">dangerous</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Permanently Delete Factory?</h3>
              <p className="text-sm text-slate-500 mb-4">
                This will permanently delete <span className="font-semibold text-slate-700">{factoryPendingDelete.name}</span> and cannot be recovered. Specifically, this deletes:
              </p>
              <ul className="text-left text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 space-y-1.5 list-disc list-inside">
                <li>Every user account at this factory (admins &amp; staff)</li>
                <li>All inspections and checklists recorded by this factory</li>
                <li>All photos and files stored for this factory in cloud storage</li>
                <li>Every other record tied to this factory (suppliers, purchase orders, categories, settings, etc.)</li>
              </ul>
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold rounded-lg px-3 py-2.5 mb-6 text-left">
                <span className="material-symbols-outlined text-[16px] shrink-0">warning</span>
                This action is irreversible. There is no way to recover this data once deleted.
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setDeleteConfirmModalOpen(false)}
                  disabled={isDeletingFactory}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteFactory}
                  disabled={isDeletingFactory}
                  className="rounded-lg bg-red-600 px-6 py-3 text-sm font-bold text-white hover:bg-red-700 shadow-sm transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  type="button"
                  style={{ flexGrow: 2 }}
                >
                  {isDeletingFactory ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>Yes, Permanently Delete</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

       {/* USER DETAIL DESCRIPTION MODAL */}
       <AnimatePresence>
         {selectedUserForDetail && (() => {
           return (
             <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
               <motion.div
                 initial={{ scale: 0.95, opacity: 0 }}
                 animate={{ scale: 1, opacity: 1 }}
                 exit={{ scale: 0.95, opacity: 0 }}
                 className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-6 shadow-xl flex flex-col gap-5 overflow-y-auto max-h-[90dvh]"
               >
                 <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                   <div className="flex items-center gap-3">
                     <img
                       src={getUserAvatarUrl(selectedUserForDetail.name, selectedUserForDetail.profileImage)}
                       alt={selectedUserForDetail.name}
                       referrerPolicy="no-referrer"
                       className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm"
                     />
                     <div>
                       <h3 className="text-base font-bold text-slate-900">{selectedUserForDetail.name}</h3>
                       <p className="text-xs text-slate-500 font-mono flex items-center gap-1.5 mt-0.5">
                         <span className="material-symbols-outlined text-[12px]">fingerprint</span>
                         Employee ID: {selectedUserForDetail.employeeId}
                       </p>
                     </div>
                   </div>
                   <button
                     onClick={() => setSelectedUserForDetail(null)}
                     className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                   >
                     <span className="material-symbols-outlined">close</span>
                   </button>
                 </div>
 
                 <div className="space-y-4">
                   {/* Account Information */}
                   <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                     <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Operator Profiles</h4>
                     <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-xs">
                       <div>
                         <span className="text-slate-400 block mb-0.5">Primary Email</span>
                         <span className="font-semibold text-slate-800 break-all">{selectedUserForDetail.email}</span>
                       </div>
                       <div>
                         <span className="text-slate-400 block mb-0.5">Facility Assignment</span>
                         <span className="font-semibold text-slate-800">{selectedUserForDetail.factoryName}</span>
                       </div>
                       <div>
                         <span className="text-slate-400 block mb-0.5">Inspection Status</span>
                         <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                           {selectedUserForDetail.status}
                         </span>
                       </div>
                       <div>
                         <span className="text-slate-400 block mb-0.5">Last Connection</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block mb-0.5">Authority Role</span>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                            selectedUserForDetail.role === 'Admin' ? 'bg-purple-150 text-purple-800 border border-purple-200 bg-purple-50' : 'bg-blue-150 text-blue-800 border border-blue-200 bg-blue-50'
                          }`}>
                            {selectedUserForDetail.role || 'Staff'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block mb-0.5">Last Connection</span>
                         <span className="font-semibold text-slate-800">{selectedUserForDetail.lastLoggedIn}</span>
                       </div>
                     </div>
                   </div>
 
                   {/* Tablet Specs */}
                   {/* Active License Modules Enabled */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[15px] text-[#136bce]">fact_check</span>
                        Active QA Modules Enabled
                      </h4>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(selectedUserForDetail.modules || ['Pilot', 'Fabric', 'Line', 'Final', 'Container']).map((mod) => (
                          <span key={mod} className="bg-blue-50/60 text-[#136bce] px-2.5 py-1 rounded-xl text-[10px] font-bold border border-blue-100/40">
                            {mod}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-blue-50/30 p-4 rounded-xl border border-blue-100/40">
                     <h4 className="text-xs font-bold text-[#136bce] uppercase tracking-widest mb-3 flex items-center gap-1">
                       <span className="material-symbols-outlined text-[15px]">tablet_mac</span>
                       Tablet Device Hardware Specs
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3.5 gap-x-4 text-xs">
                       <div className="md:col-span-2">
                         <span className="text-slate-500 block mb-0.5">Device Name</span>
                         <span className="font-bold text-slate-800">{selectedUserForDetail.deviceName || '—'}</span>
                       </div>
                       <div>
                         <span className="text-slate-500 block mb-0.5">Operating System</span>
                         <span className="font-semibold text-slate-800">{selectedUserForDetail.osVersion || '—'}</span>
                       </div>
                       <div>
                         <span className="text-slate-500 block mb-0.5">QA App Version</span>
                         <span className="font-semibold text-slate-800 bg-white px-2 py-0.5 border border-slate-200 rounded text-[11px]">
                           {selectedUserForDetail.appVersion}
                         </span>
                       </div>
                       <div className="md:col-span-2 border-t border-slate-200/50 pt-2.5 mt-1">
                         <div className="flex items-center justify-between">
                           <div>
                             <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total Usage Time</span>
                             <p className="font-bold text-slate-900 mt-0.5">{formatUsageTime(selectedUserForDetail.usageSeconds)}</p>
                           </div>
                           <div className="text-right">
                             <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Completed Inspections</span>
                             <p className="font-bold text-[#136bce] mt-0.5 text-sm">{selectedUserForDetail.inspectionsCount} batches</p>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
 
                 <div className="flex justify-end pt-2">
                   <button
                     onClick={() => setSelectedUserForDetail(null)}
                     className="px-5 py-2.5 bg-slate-950 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                   >
                     Close Hardware Telemetry
                   </button>
                 </div>
               </motion.div>
             </div>
           );
         })()}
       </AnimatePresence>
     </div>
  );
};
